'use client'

import { animate, useInView, useReducedMotion } from 'motion/react'
import { useEffect, useRef } from 'react'
import { EASE } from './animation-tokens'

const format = (n: number) => Math.round(n).toLocaleString('en-US')

/**
 * A number that counts up once when it scrolls into view. The server renders the
 * final value, so crawlers and no-JS readers get the real number.
 */
export function CountUp({ to, className }: { to: number; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, amount: 0.6 })
  const reduceMotion = useReducedMotion()

  // Start from zero if the number is still below the fold when the page hydrates.
  useEffect(() => {
    const el = ref.current
    if (!el || reduceMotion) return
    const r = el.getBoundingClientRect()
    if (r.top > window.innerHeight) el.textContent = '0'
  }, [reduceMotion])

  useEffect(() => {
    const el = ref.current
    if (!el || !inView || reduceMotion || el.textContent === format(to)) return
    const controls = animate(0, to, {
      duration: 1.2,
      ease: EASE,
      onUpdate: (v) => {
        el.textContent = format(v)
      },
    })
    return () => controls.stop()
  }, [inView, reduceMotion, to])

  return (
    <span ref={ref} className={className}>
      {format(to)}
    </span>
  )
}
